import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Compact status pill for sidebar rows. Draft is the default/empty state so
// we hide it to keep the list visually quiet — only render when the session
// has progressed. Vocabulary = B8 shared status model (mirrors the EHR
// worklist's coding_status colors): coding → needs_resolution | coded | error
// → finalized → merged_to_ehr. needs_resolution is the action queue — it gets
// the loudest (amber) treatment. Legacy values (amending/approved) retained
// for old rows.
const SIDEBAR_STATUS_STYLE = {
  coding:           { bg: '#f3f4f6', fg: '#374151', label: 'coding…' },
  needs_resolution: { bg: '#fef3c7', fg: '#b45309', label: 'needs answers' },
  coded:            { bg: '#dbeafe', fg: '#1d4ed8', label: 'coded' },
  finalized:        { bg: '#dcfce7', fg: '#15803d', label: 'finalized' },
  error:            { bg: '#fee2e2', fg: '#b91c1c', label: 'error' },
  amending:         { bg: '#fef3c7', fg: '#92400e', label: 'amending' },
  approved:         { bg: '#dcfce7', fg: '#15803d', label: 'approved' },
  merged_to_ehr:    { bg: '#ede9fe', fg: '#6d28d9', label: 'merged' },
};

const SessionStatusPill = ({ status }) => {
  if (!status || status === 'draft') return null;
  const s = SIDEBAR_STATUS_STYLE[status];
  if (!s) return null;
  // Small, quiet pill that sits inline with the date/time line — doesn't
  // compete with the session title for horizontal space.
  return (
    <span style={{
      fontSize: '0.6rem',
      fontWeight: 600,
      padding: '1px 6px',
      borderRadius: '999px',
      background: s.bg,
      color: s.fg,
      flexShrink: 0,
      lineHeight: 1.4,
    }}>
      {s.label}
    </span>
  );
};

const Sidebar = ({ sessions = [], currentSessionId, onSelectSession, onDeleteSession, onRenameSession, onNewSession, loading = false, page = 0, hasMore = false, onPageChange }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const { user, logout } = useAuth();
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const startEditing = (session) => {
    setEditingId(session.id);
    setEditTitle(session.title);
  };

  const saveEdit = (sessionId) => {
    if (editTitle.trim() && onRenameSession) {
      onRenameSession(sessionId, editTitle.trim());
    }
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const formatDate = (dateString) => {
    const normalized = /([zZ]|[+\-]\d\d:?\d\d)$/.test(dateString)
      ? dateString
      : `${dateString}Z`;
    const date = new Date(normalized);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{
      width: '280px',
      height: '100vh',
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRight: '1px solid rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Header */}
      <div style={{
        padding: '24px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: '#1d1d1f',
          margin: 0,
          letterSpacing: '-0.5px'
        }}>
          Medical Notes
        </h1>
        <p style={{
          fontSize: '0.875rem',
          color: '#64748b',
          margin: '4px 0 0 0'
        }}>
          Speech Transcription
        </p>
      </div>

      {/* New Session Button */}
      {onNewSession && (
        <div style={{ padding: '16px' }}>
          <button
            onClick={onNewSession}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              background: 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 122, 255, 0.3)';
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Session
          </button>
        </div>
      )}

      {/* Sessions List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#64748b' }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #e2e8f0',
              borderTopColor: '#1e293b',
              borderRadius: '50%',
              margin: '0 auto 12px',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{ fontSize: '0.875rem', margin: 0 }}>Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#64748b' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', opacity: 0.5 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            <p style={{ fontSize: '0.875rem', margin: 0 }}>No sessions yet</p>
            <p style={{ fontSize: '0.75rem', margin: '4px 0 0 0', opacity: 0.7 }}>Start a new session to begin</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.id}
              style={{
                marginBottom: '8px',
                borderRadius: '12px',
                background: currentSessionId === session.id ? 'rgba(0, 122, 255, 0.1)' : 'transparent',
                border: currentSessionId === session.id ? '1px solid rgba(0, 122, 255, 0.3)' : '1px solid transparent',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (currentSessionId !== session.id) {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.03)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentSessionId !== session.id) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {editingId === session.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px' }}>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit(session.id);
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      border: '1px solid #1e293b',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => saveEdit(session.id)}
                    style={{
                      padding: '6px',
                      background: '#10b981',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                  <button
                    onClick={cancelEdit}
                    style={{
                      padding: '6px',
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => onSelectSession && onSelectSession(session)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: '#64748b' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#1e293b',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {session.title}
                    </p>
                    <div style={{
                      margin: '2px 0 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.75rem',
                      color: '#64748b',
                    }}>
                      <span>{formatDate(session.updated_at)}</span>
                      <SessionStatusPill status={session.status} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditing(session);
                      }}
                      style={{
                        padding: '6px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#64748b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 122, 255, 0.1)';
                        e.currentTarget.style.color = '#007aff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#64748b';
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Delete this session?')) {
                          onDeleteSession && onDeleteSession(session.id);
                        }
                      }}
                      style={{
                        padding: '6px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#64748b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                        e.currentTarget.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#64748b';
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {(page > 0 || hasMore) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          borderTop: '1px solid rgba(0,0,0,0.06)',
        }}>
          <button
            onClick={() => onPageChange && onPageChange(page - 1)}
            disabled={page === 0 || loading}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 10px', border: 'none', borderRadius: '8px',
              background: page === 0 ? 'transparent' : 'rgba(0,0,0,0.04)',
              color: page === 0 ? '#c0c0c0' : '#1e293b',
              fontSize: '0.8rem', fontWeight: 500, cursor: page === 0 ? 'default' : 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Prev
          </button>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Page {page + 1}</span>
          <button
            onClick={() => onPageChange && onPageChange(page + 1)}
            disabled={!hasMore || loading}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 10px', border: 'none', borderRadius: '8px',
              background: !hasMore ? 'transparent' : 'rgba(0,0,0,0.04)',
              color: !hasMore ? '#c0c0c0' : '#1e293b',
              fontSize: '0.8rem', fontWeight: 500, cursor: !hasMore ? 'default' : 'pointer',
            }}
          >
            Next
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      )}

      {/* Footer - User Profile */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)'
      }}>
        {user ? (
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: 'transparent',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ':hover': {
                  background: '#f8fafc'
                }
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'white',
                flexShrink: 0
              }}>
                {user.first_name?.charAt(0) || 'U'}{user.last_name?.charAt(0) || ''}
              </div>
              <div style={{
                flex: 1,
                textAlign: 'left',
                minWidth: 0
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#1e293b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {user.first_name} {user.last_name}
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.75rem',
                  color: '#64748b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {user.email}
                </p>
              </div>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#64748b', flexShrink: 0 }}
              >
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
            </button>

            {isUserMenuOpen && (
              <div style={{
                position: 'absolute',
                left: 0,
                bottom: '60px',
                width: '100%',
                background: 'white',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden',
                zIndex: 1000
              }}>
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    navigate('/profile');
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#1e293b'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Profile
                </button>

                <div style={{ height: '1px', background: 'rgba(0, 0, 0, 0.1)' }} />

                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    logout();
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#dc2626'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#64748b',
            fontWeight: 500
          }}>
            Medical Notes System
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
